// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Is this device held in a hand? (M3-34, 08 §6.)
//
// One line of browser, in one place, because **two modules have to agree about it**. The machine
// asks in order to decide whether to offer device-motion nudging; Settings asks in order to
// decide whether to carry the control that changes the same answer. Two copies of the query
// would eventually be two different queries, and the failure is silent in the worse direction: a
// setting that exists on a device the offer is never made on says the feature is available here
// and this machine is broken.
//
// `(pointer: coarse)` rather than a user-agent string. 08 §6's rule for the whole client is that
// nothing sniffs the agent — capability is measured, and "is there a finger on this" is a
// capability with a media query of its own.

/**
 * Does this device's primary pointer report as coarse?
 *
 * False where there is no `matchMedia` at all, which is the safe direction: the only thing this
 * gates is an *offer*, and a machine that fails to offer an optional control still plays.
 */
export function coarsePointer(): boolean {
  const query = (globalThis as { matchMedia?: (q: string) => { matches: boolean } }).matchMedia
  try {
    return query?.('(pointer: coarse)').matches === true
  } catch {
    // A browser that has `matchMedia` and throws on a query it does not understand. Rare, and
    // not worth a broken page over an optional control.
    return false
  }
}

/**
 * Can this device be touched at all? (M3-33a.)
 *
 * **A different question from `coarsePointer`, and the difference is a real device.** That one asks
 * about the *primary* pointer — "is this held in a hand" — which is the right question for the
 * device-motion offer, because a laptop is not going to be shaken. This one asks whether there is
 * any touchscreen here, because the touch controls are attached unconditionally and a Windows
 * laptop with a touchscreen reports `pointer: fine` and `any-pointer: coarse`: somebody can
 * genuinely play it with a finger and would never be shown how.
 *
 * Erring in the opposite direction to `coarsePointer`, deliberately. That one gates an *offer* and
 * a missed offer costs nothing; this one gates the only written explanation of the touch controls,
 * and showing it to somebody with a mouse costs them one card they can ignore.
 */
export function touchCapable(): boolean {
  const query = (globalThis as { matchMedia?: (q: string) => { matches: boolean } }).matchMedia
  try {
    return query?.('(any-pointer: coarse)').matches === true
  } catch {
    return false
  }
}

/**
 * Is this device driven by something that can point precisely? (M3-56.)
 *
 * **The third question, and the one the key guide asks.** `coarsePointer` asks "is this held in a
 * hand", `touchCapable` asks "can this be touched at all", and this asks "is there a mouse or a
 * trackpad here" — which is a different question from `!coarsePointer()` on exactly the devices
 * that matter. A television browser reports `pointer: none`, and a player driving a d-pad has no
 * keyboard to be given a legend for.
 *
 * The `navigation.ts` rule applies unchanged: a laptop with a touchscreen reports `pointer: fine`
 * with `any-pointer: coarse` and is still being driven by a mouse, so it gets the guide. The same
 * device held as a tablet reports coarse as its primary and gets the touch hints instead.
 *
 * **True where there is no `matchMedia`**, which is the opposite default from `coarsePointer` and
 * for the reason `touchCapable` gives: that one gates an *offer* and a missed offer costs nothing,
 * while this gates the only written explanation of the keyboard. Showing it to somebody who cannot
 * use it costs one panel they collapse; withholding it costs them the plunger.
 */
export function finePointer(): boolean {
  const query = (globalThis as { matchMedia?: (q: string) => { matches: boolean } }).matchMedia
  if (typeof query !== 'function') return true
  try {
    return query('(pointer: fine)').matches === true
  } catch {
    return true
  }
}
